import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { MoreVertical } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const MenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const MenuButton = styled.button`
  background: none;
  border: 1px solid transparent;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.muted};
  font-size: 20px;
  line-height: 1;
  transition: all 0.2s ease;
  min-width: 36px;
  min-height: 36px;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    border-color: ${props => props.theme.colors.border};
    color: ${props => props.theme.colors.text};
  }
`;

const Dropdown = styled.div`
  position: fixed;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.lg || '0 4px 12px rgba(0,0,0,0.15)'};
  min-width: 160px;
  z-index: 10000;
  overflow: hidden;
`;

const MenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.variant === 'danger' ? '#dc3545' : props.theme.colors.text};
  transition: background-color 0.15s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${props => props.variant === 'danger' ? '#dc354510' : props.theme.colors.backgroundSecondary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }
`;

export const ActionMenu = ({ actions = [] }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: rect.right,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleClickOutside = (event) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updatePosition]);

  const handleItemClick = (action) => {
    setIsOpen(false);
    action.onClick();
  };

  if (actions.length === 0) return null;

  return (
    <MenuWrapper>
      <MenuButton
        ref={buttonRef}
        theme={theme}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-label="Actions"
      >
        <MoreVertical size={16} />
      </MenuButton>
      {isOpen && ReactDOM.createPortal(
        <Dropdown
          ref={dropdownRef}
          theme={theme}
          style={{ top: pos.top, left: pos.left, transform: 'translateX(-100%)' }}
        >
          {actions.map((action, index) => (
            <MenuItem
              key={index}
              theme={theme}
              variant={action.variant}
              onClick={(e) => {
                e.stopPropagation();
                handleItemClick(action);
              }}
            >
              {action.label}
            </MenuItem>
          ))}
        </Dropdown>,
        document.body
      )}
    </MenuWrapper>
  );
};
